import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "motion/react";
import {
  User as UserIcon,
  LogOut,
  Rocket,
  Menu,
  X as CloseIcon,
  Lock,
  Home,
  SquareKanban,
  Brain,
  Plug,
  Zap,
  GraduationCap,
  Users,
  Compass,
} from "lucide-react";
import { authApi } from "@/lib/auth";
import { useCompany } from "@/lib/CompanyContext";
import { useSetupProgress } from "@/lib/useSetupProgress";
import { useIsMobile } from "@/lib/useIsMobile";
import { companiesApi } from "@/api/companies";
import { knowledgeApi } from "@/api/knowledge";
import { Icon } from "./Icon";
import { NotificationsDropdown } from "./NotificationsDropdown";
import { SupportWidget } from "./SupportWidget";

type NavItem = {
  to?: string;
  href?: string;
  label: string;
  /** Label curto pro rail (62px) quando o nome completo não cabe. */
  railLabel?: string;
  Icon: React.ComponentType<{ size?: number; strokeWidth?: number; style?: React.CSSProperties }>;
};

// Ícones Lucide (repaginada do rail — substituem os AnimatedNavIcons antigos).
const NAV_ITEMS: NavItem[] = [
  { to: "/painel", label: "Painel", Icon: Home },
  { to: "/quadro", label: "Quadro", Icon: SquareKanban },
  { to: "/briefing", label: "Conhecimento", railLabel: "Bases", Icon: Brain },
  { to: "/integracoes", label: "Integrações", Icon: Plug },
  { to: "/automacoes", label: "Automações", Icon: Zap },
  { href: "https://app.themembers.com.br", label: "Aulas", Icon: GraduationCap },
  { href: "https://app.themembers.com.br", label: "Mentoria", Icon: Users },
  { to: "/escala", label: "ESCALA", Icon: Compass },
];

/** Fundo do rail — um passo mais escuro/quente que o conteúdo (destoa de leve,
 *  como no ClickUp), pro rail ler como "moldura" e o conteúdo ganhar foco. */
const RAIL_BG = "#efece4";

/** Item do rail: ícone + label de 10px SEMPRE visível embaixo (menu fechado
 *  que não precisa abrir). Ativo = quadradinho branco + ícone/label laranja. */
function RailItem({
  label,
  Icon: ItemIcon,
  active,
  locked,
  badge,
  title,
  onClick,
}: {
  label: string;
  Icon: NavItem["Icon"];
  active?: boolean;
  locked?: boolean;
  badge?: string;
  title?: string;
  onClick?: () => void;
}) {
  return (
    <span
      onClick={locked ? undefined : onClick}
      title={title ?? (locked ? "Disponível depois que você concluir os primeiros passos" : label)}
      aria-disabled={locked || undefined}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 3,
        width: 64,
        padding: "8px 2px 6px",
        borderRadius: 10,
        cursor: locked ? "not-allowed" : "pointer",
        opacity: locked ? 0.38 : 1,
        background: active ? "var(--wf-paper)" : "transparent",
        boxShadow: active ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
        color: active ? "var(--nd-orange-pun)" : "var(--wf-ink-2)",
        transition: "background 120ms, color 120ms",
        userSelect: "none",
      }}
      onMouseEnter={(e) => {
        if (!active && !locked) e.currentTarget.style.background = "rgba(255,255,255,0.65)";
      }}
      onMouseLeave={(e) => {
        if (!active) e.currentTarget.style.background = "transparent";
      }}
    >
      <ItemIcon size={19} strokeWidth={1.75} />
      <span
        style={{
          fontSize: 9.5,
          fontWeight: 600,
          letterSpacing: "0.01em",
          lineHeight: 1.1,
          maxWidth: 62,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
      {badge && (
        <span
          style={{
            position: "absolute",
            top: 2,
            right: 4,
            fontSize: 8.5,
            fontWeight: 700,
            padding: "1px 5px",
            borderRadius: 999,
            background: "var(--nd-orange)",
            color: "#fff",
          }}
        >
          {badge}
        </span>
      )}
      {locked && (
        <Lock
          size={9}
          strokeWidth={2.25}
          style={{ position: "absolute", top: 5, right: 9, color: "var(--wf-ink-3)" }}
        />
      )}
    </span>
  );
}

export function Shell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: session } = useQuery({
    queryKey: ["auth", "session"],
    queryFn: () => authApi.getSession(),
  });
  const { company, isLoading: companyLoading } = useCompany();
  const setup = useSetupProgress(company?.id);
  // Durante os primeiros passos (onboarding incompleto) o menu lateral fica
  // TRAVADO — o usuário não vaza pra Conhecimento/Aulas/Mentoria/etc. no meio do
  // fluxo. Só o atalho "Primeiros passos" continua ativo.
  const navLocked = !setup.loading && !setup.unknown && !setup.allRequiredDone;
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const dashboard = useQuery({
    queryKey: ["companies", company?.id, "dashboard"],
    queryFn: () => companiesApi.dashboard(company!.id),
    enabled: !!company?.id,
    refetchInterval: 30_000,
  });

  const me = useQuery({
    queryKey: ["auth", "me"],
    queryFn: () => authApi.me(),
    staleTime: 5 * 60_000,
  });

  const signOut = useMutation({
    mutationFn: () => authApi.signOut(),
    onSuccess: () => {
      queryClient.clear();
      navigate("/auth", { replace: true });
    },
  });

  // Reset de onboarding (ferramenta de QA, só dev). Apaga a KB de onboarding
  // e leva de volta pros primeiros passos pra testar o primeiro acesso de novo.
  const resetOnboarding = useMutation({
    mutationFn: () => knowledgeApi.resetOnboarding(company!.id),
    onSuccess: () => {
      queryClient.invalidateQueries();
      navigate("/comecar", { replace: true });
    },
  });

  // Reset de CONTA inteira (ferramenta de QA, só dev). Apaga a empresa e recria
  // fresca: zera ESCALA, KBs, issues, briefing — tudo. O id muda, então faço
  // reload limpo pra evitar queries presas no id antigo.
  const resetAccount = useMutation({
    mutationFn: () => companiesApi.reset(company!.id),
    onSuccess: () => {
      window.location.href = "/comecar";
    },
  });

  const isDev = me.data?.deploymentMode === "local_trusted" && !!company;
  const resetting = resetOnboarding.isPending || resetAccount.isPending;

  return (
    <div className="wf flex h-full">
      {/* Backdrop do drawer no mobile */}
      {isMobile && drawerOpen && (
        <div
          onClick={() => setDrawerOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(5,8,16,0.45)",
            zIndex: 199,
          }}
        />
      )}
      {/* Sidebar — desktop é um RAIL fixo de ícones (menu "fechado" que não
          precisa abrir: label de 10px sempre visível embaixo, ganha ~120px de
          largura de conteúdo). No mobile continua o drawer deslizante. */}
      <aside
        className="flex flex-col flex-shrink-0"
        style={{
          width: isMobile ? 248 : 78,
          borderRight: "1px solid var(--wf-ink-5)",
          background: isMobile ? "var(--wf-paper)" : RAIL_BG,
          padding: isMobile ? "14px 12px" : "12px 7px",
          gap: isMobile ? 14 : 8,
          alignItems: isMobile ? undefined : "center",
          ...(isMobile
            ? {
                position: "fixed" as const,
                top: 0,
                left: 0,
                bottom: 0,
                zIndex: 200,
                transform: drawerOpen ? "translateX(0)" : "translateX(-100%)",
                transition: "transform 200ms ease",
                boxShadow: drawerOpen ? "4px 0 24px rgba(5,8,16,0.18)" : "none",
                overflowY: "auto" as const,
              }
            : {}),
        }}
      >
        {/* Logo: mark reduzido no rail / horizontal no drawer mobile */}
        <div
          style={{
            padding: isMobile ? "6px 6px 2px" : "4px 0 6px",
            display: "flex",
            alignItems: "center",
            justifyContent: isMobile ? "space-between" : "center",
            gap: 8,
            width: isMobile ? undefined : "100%",
          }}
        >
          <img
            src={isMobile ? "/mohdus-logo.png" : "/mohdus-mark.png"}
            alt="Mohdus"
            style={
              isMobile
                ? { width: "100%", maxWidth: 144, display: "block" }
                : { width: 42, height: 42, display: "block", margin: "0 auto" }
            }
          />
          {isMobile && (
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              aria-label="Fechar menu"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 4,
                color: "var(--wf-ink-2)",
                flexShrink: 0,
              }}
            >
              <CloseIcon size={18} />
            </button>
          )}
        </div>

        <nav
          style={{
            display: "flex",
            flexDirection: "column",
            gap: isMobile ? 2 : 4,
            alignItems: isMobile ? undefined : "center",
          }}
          onClick={() => {
            if (isMobile) setDrawerOpen(false);
          }}
        >
          {/* Atalho "Primeiros passos" enquanto o setup não tá completo */}
          {!setup.loading && !setup.unknown && !setup.allRequiredDone &&
            (isMobile ? (
              <NavLink
                to="/comecar"
                className={({ isActive }) =>
                  `wf-nav-item${isActive ? " wf-nav-item--active" : ""}`
                }
                style={{ marginBottom: 6 }}
              >
                <Rocket size={16} strokeWidth={1.75} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1 }}>Primeiros passos</span>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    padding: "1px 6px",
                    borderRadius: 999,
                    background: "var(--nd-orange)",
                    color: "#fff",
                    flexShrink: 0,
                  }}
                >
                  {setup.requiredDone}/{setup.requiredTotal}
                </span>
              </NavLink>
            ) : (
              <NavLink to="/comecar" style={{ textDecoration: "none" }}>
                {({ isActive }) => (
                  <RailItem
                    label="Começar"
                    Icon={Rocket}
                    active={isActive}
                    badge={`${setup.requiredDone}/${setup.requiredTotal}`}
                    title="Primeiros passos"
                  />
                )}
              </NavLink>
            ))}
          {NAV_ITEMS.map((item) => {
            const ItemIcon = item.Icon;
            // ── RAIL (desktop) ──
            if (!isMobile) {
              if (navLocked) {
                return <RailItem key={item.label} label={item.railLabel ?? item.label} Icon={ItemIcon} locked />;
              }
              return item.to ? (
                <NavLink key={item.label} to={item.to} style={{ textDecoration: "none" }}>
                  {({ isActive }) => (
                    <RailItem label={item.railLabel ?? item.label} Icon={ItemIcon} active={isActive} title={item.label} />
                  )}
                </NavLink>
              ) : (
                <a
                  key={item.label}
                  href={item.href}
                  target="_blank"
                  rel="noreferrer"
                  style={{ textDecoration: "none" }}
                >
                  <RailItem label={item.railLabel ?? item.label} Icon={ItemIcon} title={`${item.label} (abre em outra aba)`} />
                </a>
              );
            }
            // ── DRAWER (mobile) — linhas com ícone + texto ──
            const inner = (
              <span style={{ display: "inline-flex", alignItems: "center", flexShrink: 0 }}>
                <ItemIcon size={16} strokeWidth={1.75} />
              </span>
            );
            if (navLocked) {
              return (
                <div
                  key={item.label}
                  className="wf-nav-item"
                  aria-disabled
                  title="Disponível depois que você concluir os primeiros passos"
                  style={{ opacity: 0.4, cursor: "not-allowed", pointerEvents: "auto" }}
                >
                  {inner}
                  <span style={{ flex: 1 }}>{item.label}</span>
                  <Lock size={12} strokeWidth={2} color="var(--wf-ink-3)" />
                </div>
              );
            }
            return item.to ? (
              <NavLink
                key={item.label}
                to={item.to}
                className={({ isActive }) =>
                  `wf-nav-item${isActive ? " wf-nav-item--active" : ""}`
                }
              >
                {inner}
                <span style={{ flex: 1 }}>{item.label}</span>
              </NavLink>
            ) : (
              <a
                key={item.label}
                href={item.href}
                target="_blank"
                rel="noreferrer"
                className="wf-nav-item"
              >
                {inner}
                <span style={{ flex: 1 }}>{item.label}</span>
              </a>
            );
          })}
        </nav>

        {(() => {
          const cents = dashboard.data?.costs.monthSpendCents ?? 0;
          const budgetCents = dashboard.data?.costs.monthBudgetCents ?? 0;
          const pct = dashboard.data?.costs.monthUtilizationPercent ?? 0;
          // 1 BRL cent = 1 ND Credit
          const fmt = (v: number) => Math.round(v).toLocaleString("pt-BR");
          const short = (v: number) =>
            v >= 1000 ? `${(v / 1000).toFixed(1).replace(".", ",")}k` : fmt(v);
          const pctClamped = Math.min(100, Math.max(0, pct));
          const overBudget = pct > 100;
          const tooltip = `ND Credits no mês: ${fmt(cents)}${budgetCents > 0 ? ` de ${fmt(budgetCents)}` : " (sem limite)"} — ver detalhamento`;

          // ── RAIL (desktop): versão compacta — ícone + valor curto + micro barra ──
          if (!isMobile) {
            return (
              <div style={{ marginTop: "auto", display: "flex", justifyContent: "center" }}>
                <NavLink to="/consumo" style={{ textDecoration: "none" }} title={tooltip}>
                  {({ isActive }) => (
                    <span
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 3,
                        width: 64,
                        padding: "8px 2px 8px",
                        borderRadius: 10,
                        background: isActive ? "var(--wf-paper)" : "transparent",
                        boxShadow: isActive ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
                      }}
                    >
                      <Zap size={17} strokeWidth={1.75} color="var(--nd-orange)" />
                      <span style={{ fontSize: 10, fontWeight: 700, color: "var(--wf-ink)" }}>
                        {short(cents)}
                      </span>
                      <span
                        style={{
                          width: 40,
                          height: 3,
                          borderRadius: 999,
                          background: "var(--wf-ink-5)",
                          overflow: "hidden",
                          display: "block",
                        }}
                      >
                        <span
                          style={{
                            display: "block",
                            height: "100%",
                            width: `${pctClamped}%`,
                            borderRadius: 999,
                            background: overBudget ? "var(--nd-orange-pun)" : "var(--nd-orange)",
                          }}
                        />
                      </span>
                    </span>
                  )}
                </NavLink>
              </div>
            );
          }

          // ── DRAWER (mobile): card completo como antes ──
          return (
            <div style={{ marginTop: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
              <NavLink
                to="/consumo"
                style={({ isActive }) => ({
                  padding: "10px 12px",
                  borderRadius: "var(--wf-radius)",
                  fontSize: 12,
                  background: isActive ? "rgba(255,112,0,0.06)" : "#fafaf8",
                  border: isActive
                    ? "1px solid var(--nd-orange)"
                    : "1px solid var(--wf-ink-5)",
                  textDecoration: "none",
                  color: "inherit",
                  display: "block",
                  transition: "border-color 120ms",
                })}
                title="Ver detalhamento do consumo"
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                  <Zap size={12} strokeWidth={1.75} color="var(--nd-orange)" />
                  <span className="wf-hand" style={{ fontSize: 11, color: "var(--wf-ink-2)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    ND Credits no mês
                  </span>
                </div>
                <div className="wf-progress" style={{ marginBottom: 6 }}>
                  <span
                    style={{
                      width: `${pctClamped}%`,
                      background: overBudget ? "var(--nd-orange-pun)" : undefined,
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    color: "var(--wf-ink-2)",
                    fontSize: 11,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>{fmt(cents)} créditos</span>
                  {budgetCents > 0 ? (
                    <span style={{ color: "var(--wf-ink-3)" }}>de {fmt(budgetCents)}</span>
                  ) : (
                    <span style={{ color: "var(--wf-ink-3)" }}>sem limite</span>
                  )}
                </div>
              </NavLink>
            </div>
          );
        })()}
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar — slim, sem greeting fixo (greeting agora vive no Hero do Painel) */}
        <header className="wf-topbar">
          {isMobile && (
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label="Abrir menu"
              style={{
                background: "none",
                border: "1px solid var(--wf-ink-5)",
                borderRadius: 8,
                padding: "5px 7px",
                cursor: "pointer",
                color: "var(--wf-ink-2)",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              <Menu size={16} />
            </button>
          )}
          <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" }}>
            {!companyLoading && company && !isMobile && (
              <span style={{ fontSize: 12, color: "var(--wf-ink-3)", fontWeight: 500 }}>
                {company.name}
              </span>
            )}
            <NotificationsDropdown />
            <AvatarMenu
              session={session}
              onProfile={() => navigate("/perfil")}
              onAdmin={
                me.data?.isAdmin ? () => navigate("/admin/users") : undefined
              }
              onResetOnboarding={
                isDev && !resetting
                  ? () => {
                      if (
                        window.confirm(
                          "Resetar só o onboarding desta empresa? O wizard de primeiros passos vai reaparecer (o resto — KBs, ESCALA, tarefas — fica intacto).\n\n(Ferramenta de QA — só funciona no ambiente de dev.)",
                        )
                      ) {
                        resetOnboarding.mutate();
                      }
                    }
                  : undefined
              }
              onResetAccount={
                isDev && !resetting
                  ? () => {
                      if (
                        window.confirm(
                          "RESETAR A CONTA INTEIRA desta empresa?\n\nApaga TUDO (onboarding, ESCALA, knowledge bases, tarefas, briefing) e recria a conta do zero, como um primeiro acesso. Não dá pra desfazer.\n\n(Ferramenta de QA — só funciona no ambiente de dev.)",
                        )
                      ) {
                        resetAccount.mutate();
                      }
                    }
                  : undefined
              }
              onSignOut={() => signOut.mutate()}
            />
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
      <SupportWidget />
    </div>
  );
}

interface SessionUser {
  user: { name: string | null; email: string | null };
}

/** Avatar com dropdown — substitui o botão "sair" do topbar.
 *  Click no avatar abre menu com Perfil + Sair. Fecha ao clicar fora ou Esc. */
function AvatarMenu({
  session,
  onProfile,
  onAdmin,
  onResetOnboarding,
  onResetAccount,
  onSignOut,
}: {
  session: SessionUser | null | undefined;
  onProfile: () => void;
  onAdmin?: () => void;
  onResetOnboarding?: () => void;
  onResetAccount?: () => void;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const name = session?.user?.name ?? null;
  const email = session?.user?.email ?? null;
  const initials = (name ?? email ?? "U")
    .split(/[\s@]/)
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Menu da conta"
        title={name ?? email ?? "Usuário"}
        style={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          border: open ? "2px solid var(--nd-orange)" : "2px solid transparent",
          padding: 0,
          background: "var(--nd-blue-deep)",
          color: "#fff",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "var(--wf-font-display)",
          fontSize: 12,
          fontWeight: 700,
          transition: "border-color 120ms ease",
        }}
      >
        {initials}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.96 }}
            transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
            role="menu"
            style={{
              position: "absolute",
              top: "calc(100% + 6px)",
              right: 0,
              minWidth: 220,
              background: "var(--wf-paper)",
              border: "1px solid var(--wf-ink-5)",
              borderRadius: 10,
              boxShadow: "0 8px 24px rgba(5,8,16,0.14)",
              padding: 6,
              zIndex: 60,
              transformOrigin: "top right",
            }}
          >
            {/* Header com nome+email */}
            <div
              style={{
                padding: "8px 10px 10px",
                borderBottom: "1px solid var(--wf-ink-5)",
                marginBottom: 4,
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--wf-ink)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {name ?? "—"}
              </div>
              {email && (
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--wf-ink-3)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {email}
                </div>
              )}
            </div>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onProfile();
              }}
              style={{
                width: "100%",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 10px",
                background: "transparent",
                border: "none",
                borderRadius: 6,
                fontSize: 13,
                fontFamily: "var(--wf-font-body)",
                color: "var(--wf-ink)",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#fff1e6";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
            >
              <UserIcon size={14} strokeWidth={1.75} color="var(--nd-orange)" />
              <span>Perfil</span>
            </button>
            {onAdmin && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onAdmin();
                }}
                style={{
                  width: "100%",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 10px",
                  background: "transparent",
                  border: "none",
                  borderRadius: 6,
                  fontSize: 13,
                  fontFamily: "var(--wf-font-body)",
                  color: "var(--wf-ink)",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#fff1e6";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                <Icon name="zap" size={14} color="var(--nd-orange)" />
                <span>Admin · Criar usuário</span>
              </button>
            )}
            {onResetOnboarding && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onResetOnboarding();
                }}
                style={{
                  width: "100%",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 10px",
                  background: "transparent",
                  border: "none",
                  borderRadius: 6,
                  fontSize: 13,
                  fontFamily: "var(--wf-font-body)",
                  color: "var(--wf-ink)",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#fff1e6";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                <Icon name="refresh" size={14} color="var(--nd-orange)" />
                <span>QA · Resetar onboarding</span>
              </button>
            )}
            {onResetAccount && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onResetAccount();
                }}
                style={{
                  width: "100%",
                  textAlign: "left",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "8px 10px",
                  background: "transparent",
                  border: "none",
                  borderRadius: 6,
                  fontSize: 13,
                  fontFamily: "var(--wf-font-body)",
                  color: "#c0392b",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#fdecea";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                }}
              >
                <Icon name="refresh" size={14} color="#c0392b" />
                <span>QA · Resetar conta (tudo)</span>
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
              style={{
                width: "100%",
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 10px",
                background: "transparent",
                border: "none",
                borderRadius: 6,
                fontSize: 13,
                fontFamily: "var(--wf-font-body)",
                color: "var(--wf-ink)",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#fff1e6";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
            >
              <LogOut size={14} strokeWidth={1.75} color="var(--wf-ink-3)" />
              <span>Sair</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
